import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaBus,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaEdit,
  FaTrash,
  FaIdCard,
  FaUsers,
} from "react-icons/fa";
import icons_school_bus from "../resources/icons/icons_school_bus.jpeg";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import COLORS from "../resources/colors/COLORS";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import MdlAddTransportVehicles from "../dialog/MdlAddTransportVehicles";
import MdlEditTransportVehicles from "../dialog/MdlEditTransportVehicles";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Math.round(n).toLocaleString("en-IN");
}

function vehicleImageSrc(vehicle) {
  const url = String(
    vehicle?.vehicle_image_url || vehicle?.vehicle_image || ""
  ).trim();
  if (!url || /^null$/i.test(url)) return icons_school_bus;
  return url;
}

function resolveSessionId() {
  const session = SessionManager.getCurrentSession();
  if (Array.isArray(session) && session[0]?.session_id) {
    return String(session[0].session_id);
  }
  if (session && typeof session === "object" && session.session_id) {
    return String(session.session_id);
  }
  return "";
}

const VehicleThumb = memo(function VehicleThumb({ vehicle }) {
  const [failed, setFailed] = useState(false);
  const src = vehicleImageSrc(vehicle);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  return (
    <span className="tv-card__thumb" aria-hidden>
      {!failed ? (
        <img src={src} alt="" onError={() => setFailed(true)} />
      ) : (
        <FaBus />
      )}
    </span>
  );
});

const VehicleCard = memo(function VehicleCard({
  vehicle,
  index,
  onEdit,
  onDelete,
  deleting,
}) {
  const typeLabel = vehicle?.vehicle_type || "—";
  const studentCount = Number(vehicle?.student_count) || 0;

  return (
    <article className="tr-card tv-card" style={{ "--tr-i": index }}>
      <div className="tr-card__head tv-card__head">
        <span className="tr-card__index" aria-hidden>
          {String(index + 1).padStart(2, "0")}
        </span>
        <VehicleThumb vehicle={vehicle} />
        <div className="tr-card__main min-w-0">
          <div className="tr-card__top">
            <h3 className="tr-card__name">
              {vehicle.vehicle_name || "Unnamed vehicle"}
            </h3>
            <p className="tr-card__path" title={vehicle.vehicle_number || ""}>
              <FaIdCard className="tr-card__path-arrow" aria-hidden />
              {vehicle.vehicle_number || "No number"}
            </p>
          </div>
          <div className="tr-card__chips">
            <span className="tr-chip tr-chip--stops tv-chip--type">
              <FaBus aria-hidden />
              {typeLabel}
            </span>
            <span
              className={`tr-chip${
                studentCount > 0 ? " tr-chip--distance" : " tr-chip--muted"
              }`}
              title={`${studentCount} student${studentCount === 1 ? "" : "s"}`}
            >
              <FaUsers aria-hidden />
              {formatCount(studentCount)} student
              {studentCount === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </div>

      <div className="tr-card__actions">
        <button
          type="button"
          className="tr-card__edit"
          onClick={() => onEdit?.(vehicle)}
          disabled={deleting}
          title="Edit vehicle"
          aria-label={`Edit ${vehicle.vehicle_name || "vehicle"}`}
        >
          <FaEdit aria-hidden /> Edit
        </button>
        <button
          type="button"
          className="tv-card__delete"
          onClick={() => onDelete?.(vehicle)}
          disabled={deleting}
          title="Delete vehicle"
          aria-label={`Delete ${vehicle.vehicle_name || "vehicle"}`}
        >
          <FaTrash aria-hidden />
          {deleting ? "…" : "Delete"}
        </button>
      </div>
    </article>
  );
});

const TransportVehicles = () => {
  const loggedIn = localStorage.getItem("token") != null;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const abortRef = useRef(null);

  const loadVehicles = useCallback(async ({ soft = false } = {}) => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setVehicles([]);
      myToast.notify_warning("School code is missing.");
      setLoading(false);
      setRefreshing(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (soft) setRefreshing(true);
    else setLoading(true);

    try {
      const params = { school_code: schoolCode };
      const sessionId = resolveSessionId();
      if (sessionId) params.session_id = sessionId;

      const res = await axios.get(`${API_BASE()}getAllVehicles.php`, {
        params,
        signal: controller.signal,
      });

      const payload = res?.data;
      if (payload?.api_status === "OK" && Array.isArray(payload.data)) {
        setVehicles(payload.data);
      } else {
        setVehicles([]);
        const remark = String(payload?.api_remark || "");
        if (
          payload?.api_status !== "OK" &&
          remark &&
          !/no vehicles?/i.test(remark) &&
          !/no record/i.test(remark)
        ) {
          myToast.notify_warning(remark);
        }
      }
    } catch (error) {
      if (
        axios.isCancel?.(error) ||
        error?.name === "CanceledError" ||
        error?.code === "ERR_CANCELED"
      ) {
        return;
      }
      console.error("[TransportVehicles] load error", error);
      setVehicles([]);
      myToast.notify_warning("Network error loading vehicles.");
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    loadVehicles();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [loadVehicles]);

  const filteredVehicles = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter((vehicle) => {
      const hay = [
        vehicle.vehicle_name,
        vehicle.vehicle_number,
        vehicle.vehicle_type,
        vehicle.vehicle_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [vehicles, searchTerm]);

  const typeSummary = useMemo(() => {
    const map = {};
    vehicles.forEach((v) => {
      const key = String(v?.vehicle_type || "Other").trim() || "Other";
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([type, count]) => `${count} ${type}`)
      .join(" · ");
  }, [vehicles]);

  const totalStudents = useMemo(
    () =>
      vehicles.reduce(
        (sum, v) => sum + (Number(v?.student_count) || 0),
        0
      ),
    [vehicles]
  );

  const handleDeleteVehicle = useCallback(async (vehicle) => {
    const vehicleId = vehicle?.vehicle_id;
    if (!vehicleId) {
      myToast.notify_warning("Vehicle id is missing.");
      return;
    }

    const label = vehicle?.vehicle_name || vehicle?.vehicle_number || "this vehicle";
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) {
      return;
    }

    const schoolCode = SessionManager.getSchoolCode();
    const staff = SessionManager.getStaffDetails();
    const staffId = staff?.staff_id;
    if (!schoolCode || !staffId) {
      myToast.notify_warning("School or staff details are missing.");
      return;
    }

    setDeletingId(String(vehicleId));
    try {
      const res = await axios.get(`${API_BASE()}deleteVehicle.php`, {
        params: {
          vehicle_id: vehicleId,
          staff_id: staffId,
          school_code: schoolCode,
        },
      });
      const payload = res?.data;
      if (payload?.api_status === "OK") {
        myToast.notify_success("Vehicle deleted successfully.");
        setVehicles((prev) =>
          prev.filter((v) => String(v?.vehicle_id) !== String(vehicleId))
        );
      } else {
        myToast.notify_warning(
          payload?.api_remark || "Could not delete vehicle."
        );
      }
    } catch (error) {
      console.error("[TransportVehicles] delete error", error);
      myToast.notify_error("Failed to delete vehicle.");
    } finally {
      setDeletingId(null);
    }
  }, []);

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page tr-page tv-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Transport Vehicles</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBus />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Transport Vehicles</h1>
              <p className="sm-hero__sub">
                {formatCount(vehicles.length)} vehicle
                {vehicles.length === 1 ? "" : "s"} ·{" "}
                {formatCount(totalStudents)} student
                {totalStudents === 1 ? "" : "s"}
                {typeSummary ? ` · ${typeSummary}` : ""}
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <FaSearch className="tr-search-icon" aria-hidden />
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search name, number, or type…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search transport vehicles"
              />
            </div>
            <button
              type="button"
              onClick={() => loadVehicles({ soft: true })}
              disabled={refreshing || loading}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh list"
              aria-label="Refresh list"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => setShowAddVehicle(true)}
              className="tr-hero-link tr-hero-link--solid"
            >
              <FaPlus aria-hidden /> Add Vehicle
            </button>
          </div>
        </header>

        <div className="tr-content">
          <div className="tr-section-head">
            <div className="min-w-0">
              <h2 className="tr-section-head__title">Fleet registry</h2>
              <p className="tr-section-head__sub">
                Manage bus details used across routes and students
              </p>
            </div>
            <span className="tr-section-head__count">
              {`${filteredVehicles.length}${
                searchTerm.trim() ? ` of ${vehicles.length}` : ""
              } vehicle${filteredVehicles.length === 1 ? "" : "s"}`}
            </span>
          </div>

          {loading ? (
            <div className="tr-loading">
              <MyLoader />
            </div>
          ) : filteredVehicles.length === 0 ? (
            <div className="sm-empty tr-empty">
              <div className="tr-empty__circle" aria-hidden>
                <FaBus />
              </div>
              <p className="sm-empty__title">
                {vehicles.length === 0
                  ? "No vehicles yet"
                  : "No matches for your search"}
              </p>
              <p className="sm-empty__hint">
                {vehicles.length === 0
                  ? "Add a vehicle to start assigning transport to students."
                  : "Try a different name, number, or type."}
              </p>
              {vehicles.length === 0 ? (
                <button
                  type="button"
                  className="tr-empty-cta"
                  onClick={() => setShowAddVehicle(true)}
                >
                  <FaPlus aria-hidden /> Add New Vehicle
                </button>
              ) : null}
            </div>
          ) : (
            <div className="tr-list" role="list">
              {filteredVehicles.map((vehicle, index) => (
                <VehicleCard
                  key={vehicle.vehicle_id || index}
                  vehicle={vehicle}
                  index={index}
                  onEdit={setEditVehicle}
                  onDelete={handleDeleteVehicle}
                  deleting={
                    String(deletingId) === String(vehicle.vehicle_id)
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {showAddVehicle ? (
        <MdlAddTransportVehicles
          actionClose={() => setShowAddVehicle(false)}
          onSuccess={() => loadVehicles({ soft: true })}
        />
      ) : null}

      {editVehicle ? (
        <MdlEditTransportVehicles
          vehicle={editVehicle}
          actionClose={() => setEditVehicle(null)}
          onSuccess={() => loadVehicles({ soft: true })}
        />
      ) : null}
    </div>
  );
};

export default TransportVehicles;
